import { Space } from 'antd';
import { isEmpty, reverse, sortBy } from 'lodash';
import moment from 'moment';
import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PastProjectStatus } from 'api/project';
import { StatusUpdateStatuses } from 'api/status-updates';
import { Avatar } from 'components/atoms/avatar';
import { EmptyCell } from 'components/atoms/empty-cell/index';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { ListWithLink } from 'components/atoms/list-with-links';
import { Tag } from 'components/atoms/tag';
import { RecentStatus } from 'components/molecules/recent-status/index';
import { StatusPopover } from 'components/molecules/status-popover/index';
import { Label01, Text02 } from 'components/styles';
import { useTranslate } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { getProjectStatusUpdatesDetailUrl } from 'utils/urls';
import { getStatusData } from '../constants';

const Date = styled.span`
  width: 60px;
  display: inline-block;
  ${Label01};
  ${Text02};
`;

export const popoverContent = ({
  projectId,
  data,
  translate,
}: {
  projectId: number;
  data: PastProjectStatus[];
  translate;
}) => {
  return (
    <ListWithLink
      dataSource={data}
      renderItem={(item) => {
        const currentStatus = getStatusData(
          StatusUpdateStatuses,
          item.projectStatusName,
        );

        const label =
          currentStatus?.label?.toLowerCase() === StatusUpdateStatuses.none.key
            ? translate('no_status')
            : currentStatus?.label;

        return (
          <ListWithLink.Item>
            <Link
              to={getProjectStatusUpdatesDetailUrl(
                projectId,
                item.projectStatusId,
              )}
            >
              <Space>
                <Date>
                  {item.updatedAt && (
                    <HumanisedDate timestamp={moment(item.updatedAt)} />
                  )}
                </Date>
                <Avatar
                  name={fullName(item.updatedBy)}
                  src={item.updatedBy.photoUrl}
                />
                <Tag color={currentStatus?.color}>{label}</Tag>
              </Space>
            </Link>
          </ListWithLink.Item>
        );
      }}
    />
  );
};

export const LastFiveUpdates: FC<{
  data: PastProjectStatus[];
  projectId: number;
  subTitle: string;
}> = ({ data, projectId, subTitle }) => {
  const translate = useTranslate();

  if (!isEmpty(data)) {
    const sortedUpdates = reverse(sortBy(data, 'updatedAt')); // sort in the decending order

    // returns the array of objects with type:<value>
    const statusContent = sortedUpdates.map((update) => {
      const currentUpdate = getStatusData(
        StatusUpdateStatuses,
        update?.projectStatusName,
      );

      return {
        id: update.projectStatusId,
        type: currentUpdate?.type,
      };
    });

    return (
      <StatusPopover
        title={translate('project.update')}
        content={popoverContent({ projectId, data: sortedUpdates, translate })}
        subTitle={subTitle}
      >
        <div>
          <RecentStatus data={statusContent} />
        </div>
      </StatusPopover>
    );
  }

  return <EmptyCell />;
};
